import { EnumSelect } from '@core/components/EnumSelect/EnumSelect';
import { useBlockAllKeyboardShortcuts, useEffectOnce } from '@core/logic';
import { useIsTelekomProject } from '@core/logic/hooks/useIsTelekomProject';
import { useAppDispatch } from '@core/redux/hooks';
import {
  useGetContainableSplineElementsQuery,
  useGetSplineElementContainedAttributesQuery,
} from '@core/redux/query';
import { DotFilled, TrashcanOutline, XOutline } from '@deepup/icons';
import { elementThunks, elementTypeSelectors } from '@elements/redux';
import {
  COMMENT_PREFIX,
  NO_GEO,
  PartialSplineEl,
  SplineElement,
  SplineElementKind,
  SplineElementType,
  UsageType,
  usageTypes,
} from '@elements/types';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  ListItem,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { setEditMode } from '@projects/edit-modes';
import { EditMode } from '@projects/edit-modes/types';
import { HouseLeadVariantSelector } from '@projects/pages/detail/HouseLeadVariantSelector';
import { colorsForTelekomDBB } from '@projects/pages/detail/PipeFittings/pipeColors';
import { viewer3dActions, viewer3dSelectors } from '@viewer3D/redux';
import { cloneDeep, isEqual, set } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { ChildElementSelect, ElementDialogProps, ParentSelectAutocomplete } from './ElementDialog';

const initialElement: PartialSplineEl = {
  name: '',
  splineElementTypeId: '',
  parentPointElementId: null,
  address: null,
  comment: '',
  usageType: undefined,
};

export const DEFAULT_COMMENT = 'Element labeled according to Plandata';
export const SYNERGY = 'Synergy';
const initialElementKind: SplineElementKind = 'SPEEDPIPE_BUNDLE';

const kindTabs: [string, SplineElementKind][] = [
  ['Bundle', 'SPEEDPIPE_BUNDLE'],
  ['Single Pipe', 'SPEEDPIPE'],
  ['Protective Pipe', 'PROTECTIVE_PIPE'],
  ['Other', 'OTHER'],
];

const useConnect = ({
  elementToUpdate,
  childSplineElements,
}: {
  elementToUpdate?: SplineElement;
  childSplineElements: SplineElement[] | undefined | null;
}) => {
  const dispatch = useAppDispatch();
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);
  const { data: containedAttributesData, refetch: refetchContainedAttributes } =
    useGetSplineElementContainedAttributesQuery(elementToUpdate?.id);
  const { refetch: refetchContainableElements } = useGetContainableSplineElementsQuery(
    elementToUpdate?.id,
  );
  const { isTelekomProject } = useIsTelekomProject();
  const [element, setElement] = useState<PartialSplineEl>(elementToUpdate ?? initialElement);
  const selectedElementType = useSelector(
    elementTypeSelectors.selectById(element.splineElementTypeId),
  );
  const [selectedKind, setSelectedKind] = useState<SplineElementKind>(
    selectedElementType?.kind ?? initialElementKind,
  );
  const rawElementTypes = useSelector(
    elementTypeSelectors.selectByKind(selectedKind),
    shallowEqual,
  );
  const elementTypesBySelectedKind = useMemo(() => {
    return [...rawElementTypes].sort((a, b) => b.name.localeCompare(a.name));
  }, [rawElementTypes]);

  const updateProp = (path: string, value: unknown) => {
    setElement((e) => set(cloneDeep(e), path, value));
  };

  const getCanSubmit = () => {
    if (isTelekomProject) {
      return true;
    }

    if (element === initialElement) {
      return false;
    }

    if (isEqual(element, elementToUpdate ?? initialElement)) {
      if (Array.isArray(childSplineElements)) {
        return true;
      }
      return false;
    }

    return true;
  };

  const onSubmit = async () => {
    const canSubmit = getCanSubmit();

    if (!canSubmit) {
      return;
    }

    // Clean up address: if all fields are empty/undefined, set address to null
    const cleanedElement = { ...element };
    if (
      element.address &&
      !element.address.street &&
      element.address.number === undefined &&
      !element.address.addition
    ) {
      cleanedElement.address = null;
    }

    const { payload } = elementToUpdate
      ? await dispatch(
          elementThunks.patch({
            path: ['projects', projectId!, 'splineElements', elementToUpdate.id],
            body: cleanedElement,
          }),
        )
      : await dispatch(
          elementThunks.post({
            path: ['projects', projectId!, 'splineElements'],
            body: {
              ...cleanedElement,
              projectId,
            },
          }),
        );

    const nextElement = payload as SplineElement;

    const containerId = elementToUpdate?.id ?? nextElement.id;

    if (containerId && Array.isArray(childSplineElements)) {
      const currentContainedIds =
        containedAttributesData?.map((attr) => attr.childSplineElementId) ?? [];
      const newContainedIds = childSplineElements?.map((el) => el.id) ?? [];

      const elementsToAdd =
        childSplineElements?.filter((el) => !currentContainedIds.includes(el.id)) ?? [];
      const attributesToRemove =
        containedAttributesData?.filter(
          (attr) => !newContainedIds.includes(attr.childSplineElementId),
        ) ?? [];

      await Promise.all([
        ...elementsToAdd.map(async (child) => {
          await dispatch(
            elementThunks.createAttribute({
              path: ['projects', projectId!, 'splineElements', 'attributes'],
              body: {
                childSplineElementId: child.id,
                containerId,
              },
            }),
          );
        }),
        ...attributesToRemove.map(async (attr) => {
          await dispatch(
            elementThunks.removeAttribute({
              path: ['projects', projectId!, 'splineElements', 'attributes', attr.id],
            }),
          );
        }),
      ]);

      await Promise.all([refetchContainedAttributes(), refetchContainableElements()]);
    }

    // reselect element to navigate to it again (dirty hack!)
    dispatch(viewer3dActions.setSelectedElementIds([]));
    dispatch(viewer3dActions.setSelectedElementIds([nextElement.id]));

    return nextElement;
  };

  const isCurrentNameAutoGenerated = elementTypesBySelectedKind.some(
    (t) => t.name === element.name,
  );
  const setElementType = (nextTypeId: string) => {
    if (!element.name || isCurrentNameAutoGenerated) {
      // name prop is empty or autofilled and unmodified by User
      const nextType = elementTypesBySelectedKind.find((t) => t.id === nextTypeId);
      updateProp('name', nextType?.name);
    }
    updateProp('splineElementTypeId', nextTypeId);
  };

  const handleChangeKind = (_: unknown, kind: SplineElementKind) => {
    setSelectedKind(kind);
    isCurrentNameAutoGenerated && setElement(initialElement);

    if (kind === 'SPEEDPIPE') {
      updateProp('usageType', 'HOUSE_LEAD');
    } else if (kind === 'SPEEDPIPE_BUNDLE') {
      updateProp('usageType', 'TRASS');
    } else {
      updateProp('usageType', undefined);
    }
  };

  return {
    element,
    updateProp,
    onSubmit,
    selectedKind,
    handleChangeKind,
    setElementType,
    elementTypesBySelectedKind,
    isTelekomProject,
  };
};

const extractColorFromComment = (comment?: string): string | null => {
  if (!comment) return null;

  // Sort colors by length (longest first) to avoid partial matches
  const sortedColors = [...colorsForTelekomDBB].sort((a, b) => b.name.length - a.name.length);

  for (const colorOption of sortedColors) {
    if (comment.includes(colorOption.name)) {
      return colorOption.name;
    }
  }

  return null;
};

const extractCommentFromCommentField = (commentFieldValue?: string) => {
  if (!commentFieldValue) {
    return '';
  }

  const commentProperties = commentFieldValue.split('.');

  const comment = commentProperties.find((p) => p.startsWith(COMMENT_PREFIX));

  return comment?.replaceAll(COMMENT_PREFIX, '');
};

const getCommentToAdd = (
  labelAsPlannedChecked: boolean,
  hasNoGeoAvailable: boolean,
  synergySelected: boolean,
  annotation?: string,
  telekomColor?: string,
) => {
  const parts = [];
  if (labelAsPlannedChecked) parts.push(DEFAULT_COMMENT);
  if (hasNoGeoAvailable) parts.push(NO_GEO);
  if (synergySelected) parts.push(SYNERGY);
  if (telekomColor) parts.push(telekomColor);
  if (annotation) parts.push(`${COMMENT_PREFIX}${annotation}`);

  return parts.join('.');
};

export const SplineElementProperties = ({
  onDelete,
  ...props
}: ElementDialogProps<SplineElement> & {
  onNext: (editedSplineElement: SplineElement) => void;
}) => {
  useBlockAllKeyboardShortcuts();
  useEffectOnce(() => {
    setEditMode(EditMode.Element);
  });
  const { elementToUpdate, onClose } = props;
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [childSplineElements, setChildSplineElements] = useState<SplineElement[] | undefined>();

  const {
    selectedKind,
    handleChangeKind,
    element,
    updateProp,
    setElementType,
    elementTypesBySelectedKind,
    onSubmit,
    isTelekomProject,
  } = useConnect({ elementToUpdate, childSplineElements });

  const handleSubmit = async (goToSMAs: boolean) => {
    const nextElement = await onSubmit();

    if (!nextElement) {
      return;
    }

    if (goToSMAs) {
      props.onNext(nextElement);
      return;
    }

    onClose();
  };

  let computedUsage: UsageType | undefined = element.usageType as UsageType;
  if (!computedUsage) {
    if (selectedKind === 'SPEEDPIPE') {
      computedUsage = 'HOUSE_LEAD';
    } else if (selectedKind === 'SPEEDPIPE_BUNDLE') {
      computedUsage = 'TRASS';
    }
  }

  const shouldDisableSubmit =
    isTelekomProject && computedUsage === 'HOUSE_LEAD' && element?.houseLeadVariant === undefined;

  const updateUsageType = (event: SelectChangeEvent<UsageType | UsageType[]>) => {
    const val = event.target.value;
    const next = Array.isArray(val) ? val[0] : val;
    updateProp('usageType', next);
  };

  // Use separate state variables for checkbox states
  const [isLabeledAsPlanned, setIsLabeledAsPlanned] = useState<boolean>(
    elementToUpdate?.comment?.includes(DEFAULT_COMMENT) || false,
  );
  const [hasNoGeoAvailable, setHasNoGeoAvailable] = useState<boolean>(
    elementToUpdate?.comment?.includes(NO_GEO) || false,
  );
  const [isSynergySelected, setIsSynergySelected] = useState<boolean>(
    elementToUpdate?.comment?.includes(SYNERGY) || false,
  );
  const [selectedTelekomCommentColor, setSelectedTelekomCommentColor] = useState<string | null>(
    extractColorFromComment(elementToUpdate?.comment),
  );
  const [comment, setComment] = useState(extractCommentFromCommentField(elementToUpdate?.comment));

  // Update checkbox states when element comment changes
  useEffect(() => {
    if (element.comment) {
      setIsLabeledAsPlanned(element.comment.includes(DEFAULT_COMMENT));
      setHasNoGeoAvailable(element.comment.includes(NO_GEO));
      setIsSynergySelected(element.comment.includes(SYNERGY));
    }
  }, [element.comment]);

  const getSubmitButtonLabel = () =>
    elementToUpdate ? 'Update Spline Element' : 'Add Spline Element';

  const getCreateButtonLabel = () => (elementToUpdate ? 'Update' : 'Create');

  return (
    <>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Tabs value={selectedKind} centered onChange={handleChangeKind} variant="fullWidth">
              {kindTabs.map(([label, kind]) => (
                <Tab value={kind} label={label} key={kind} iconPosition="start" />
              ))}
            </Tabs>
            <Divider />
          </Grid>
          <Grid size={12}>
            <Autocomplete
              key={selectedKind + 'type'} // empties input when the selectedKind changes
              value={
                elementTypesBySelectedKind.find((t) => t.id === element?.splineElementTypeId) ||
                null
              }
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
              renderInput={(params) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  label="Element Type"
                  required
                  fullWidth
                />
              )}
              onChange={(e, value, reason) => {
                if (reason === 'selectOption') setElementType((value as SplineElementType).id);
                if (reason === 'clear') {
                  setElementType('');
                  updateProp('name', '');
                }
              }}
              options={elementTypesBySelectedKind}
              componentsProps={{ paper: { elevation: 24 } }}
              renderOption={(props, option) => (
                <ListItem
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  dense
                  key={option.id}
                >
                  <Stack
                    direction="row"
                    fontSize={(theme) => theme.typography.body2.fontSize}
                    lineHeight={1}
                    alignItems="center"
                    width="100%"
                    gap={2}
                  >
                    <Typography width="35%">{option.name}</Typography>
                    <Stack direction="row">
                      <DotFilled fill={option.color} height={24} width={24} />
                      <Typography>{option.color}</Typography>
                    </Stack>
                  </Stack>
                </ListItem>
              )}
            />
          </Grid>
          {selectedKind !== 'OTHER' && (
            <Grid size={12}>
              <EnumSelect
                label="Usage type"
                options={usageTypes}
                value={computedUsage}
                onChange={updateUsageType}
              />
            </Grid>
          )}
          <Grid size={12}>
            <TextField
              fullWidth
              required
              inputProps={{ 'aria-label': 'Name' }}
              label="Element Name"
              value={element.name}
              onChange={(e) => updateProp('name', e.target.value)}
              InputProps={{
                ref: nameInputRef,
                endAdornment: (
                  <IconButton
                    size="medium"
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation();
                      updateProp('name', '');
                      // set the cursor in the text field in order for the User to start typing
                      nameInputRef.current?.click();
                    }}
                  >
                    <XOutline />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid size={12}>
            <ParentSelectAutocomplete elementToUpdate={elementToUpdate} updateProp={updateProp} />
          </Grid>
          {selectedKind === 'PROTECTIVE_PIPE' && (
            <Grid size={12}>
              <ChildElementSelect
                elementIdToExclude={elementToUpdate?.id}
                onChange={(newChildSplineElements) =>
                  setChildSplineElements(newChildSplineElements)
                }
                currentChildSplineElements={childSplineElements}
              />
            </Grid>
          )}
          {(computedUsage === 'HOUSE_LEAD' || computedUsage === 'TRASS') && (
            <>
              <Grid size={10}>
                <TextField
                  fullWidth
                  inputProps={{ 'aria-label': 'Street' }}
                  label="Street"
                  value={element?.address?.street || ''}
                  onChange={(e) => updateProp('address.street', e.target.value || undefined)}
                />
              </Grid>
              <Grid size={2}>
                <TextField
                  fullWidth
                  label="Nr."
                  inputProps={{ 'aria-label': 'Number' }}
                  value={element?.address?.number ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      updateProp('address.number', undefined);
                      return;
                    }
                    if (isNaN(Number(value))) return;
                    updateProp('address.number', Number(value));
                  }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  inputProps={{ 'aria-label': 'Addition' }}
                  label="Addition"
                  value={element?.address?.addition || ''}
                  onChange={(e) => updateProp('address.addition', e.target.value || undefined)}
                />
              </Grid>
            </>
          )}
          <Grid size={12}>
            <Stack direction="column" spacing={2} mb={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isLabeledAsPlanned}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setIsLabeledAsPlanned(newValue);
                      updateProp(
                        'comment',
                        getCommentToAdd(
                          newValue,
                          hasNoGeoAvailable,
                          isSynergySelected,
                          comment,
                          selectedTelekomCommentColor || undefined,
                        ),
                      );
                    }}
                  />
                }
                label={DEFAULT_COMMENT}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasNoGeoAvailable}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setHasNoGeoAvailable(newValue);
                      updateProp(
                        'comment',
                        getCommentToAdd(
                          isLabeledAsPlanned,
                          newValue,
                          isSynergySelected,
                          comment,
                          selectedTelekomCommentColor || undefined,
                        ),
                      );
                    }}
                  />
                }
                label="No Geo"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSynergySelected}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setIsSynergySelected(newValue);
                      updateProp(
                        'comment',
                        getCommentToAdd(
                          isLabeledAsPlanned,
                          hasNoGeoAvailable,
                          newValue,
                          comment,
                          selectedTelekomCommentColor || undefined,
                        ),
                      );
                    }}
                  />
                }
                label={SYNERGY}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!element?.labelingConfidence}
                    onChange={(e) =>
                      updateProp('labelingConfidence', e.target.checked ? 'BEST_GUESS' : null)
                    }
                  />
                }
                label="Best guess"
              />
              <Autocomplete
                value={
                  selectedTelekomCommentColor
                    ? colorsForTelekomDBB.find(
                        (item) => item.name === selectedTelekomCommentColor,
                      ) || null
                    : null
                }
                onChange={(_, colorOption) => {
                  setSelectedTelekomCommentColor(colorOption ? colorOption.name : null);
                  updateProp(
                    'comment',
                    getCommentToAdd(
                      isLabeledAsPlanned,
                      hasNoGeoAvailable,
                      isSynergySelected,
                      comment,
                      colorOption ? colorOption.name : undefined,
                    ),
                  );
                }}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  return typeof option === 'string' ? option : option.name;
                }}
                options={colorsForTelekomDBB}
                isOptionEqualToValue={(option, value) => {
                  return option.name === value.name;
                }}
                renderInput={(params) => (
                  <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    label={isTelekomProject ? 'Telekom comment color' : 'Comment color'}
                    fullWidth
                  />
                )}
                componentsProps={{ paper: { elevation: 24 } }}
                renderOption={(props, colorOption) => {
                  return (
                    <ListItem
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...props}
                      dense
                      key={colorOption.name}
                      sx={{ py: 1 }}
                    >
                      <Stack
                        fontSize={(theme) => theme.typography.body1.fontSize}
                        lineHeight={1.5}
                        alignItems="center"
                        direction="row"
                        gap={2}
                      >
                        <Typography>{colorOption.name}</Typography>
                        <Box
                          component="span"
                          sx={{
                            backgroundColor: colorOption.color,
                            width: 24,
                            height: 24,
                            display: 'inline-block',
                            borderRadius: '50%',
                          }}
                        />
                      </Stack>
                    </ListItem>
                  );
                }}
              />
              <TextField
                value={comment}
                label="Comment"
                onChange={(e) => {
                  setComment(e.target.value);
                  updateProp(
                    'comment',
                    getCommentToAdd(
                      isLabeledAsPlanned,
                      hasNoGeoAvailable,
                      isSynergySelected,
                      e.target.value,
                      selectedTelekomCommentColor || undefined,
                    ),
                  );
                }}
              />
            </Stack>
            {(computedUsage === 'HOUSE_LEAD' || computedUsage === 'TRASS') && (
              <Stack spacing={2}>
                <HouseLeadVariantSelector
                  selectedVariant={element.houseLeadVariant}
                  toggleVariant={(variant) => updateProp('houseLeadVariant', variant)}
                />
              </Stack>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions disableSpacing>
        <Stack width="100%" p={2}>
          <Stack direction="row" gap={2} flexGrow={1}>
            {isTelekomProject && (
              <Button
                onClick={() => handleSubmit(true)}
                variant="outlined"
                disabled={!element.name || !element.splineElementTypeId || shouldDisableSubmit}
              >
                Assign SMAs
              </Button>
            )}
            {!isTelekomProject && onDelete && (
              <Button
                variant="text"
                color="error"
                startIcon={<TrashcanOutline />}
                onClick={onDelete}
              >
                Delete element
              </Button>
            )}
            <Box component="div" flexGrow={1} />
            {isTelekomProject && onDelete && (
              <Button
                variant="text"
                color="error"
                startIcon={<TrashcanOutline />}
                onClick={onDelete}
              >
                Delete element
              </Button>
            )}
            <Button onClick={onClose} variant="outlined" color="secondary">
              Cancel
            </Button>
            {isTelekomProject ? (
              <Button
                onClick={() => handleSubmit(false)}
                variant="contained"
                disabled={!element.name || !element.splineElementTypeId || shouldDisableSubmit}
              >
                {getCreateButtonLabel()}
              </Button>
            ) : (
              <Button
                onClick={() => handleSubmit(false)}
                variant="contained"
                disabled={!element.name || !element.splineElementTypeId || shouldDisableSubmit}
              >
                {getSubmitButtonLabel()}
              </Button>
            )}
          </Stack>
        </Stack>
      </DialogActions>
    </>
  );
};
